'use client';

import React from 'react';
import Link from 'next/link';
import { useTranslation } from '@/hooks/useTranslation';

interface Category {
    id: string;
    nameKey: string;
    descKey: string;
    image: string;
    link: string;
    gradient: string;
}

const ProductCategories = () => {
    const { t } = useTranslation();

    const categories: Category[] = [
        {
            id: 'yarn',
            nameKey: 'yarnSpinning',
            descKey: 'yarnSpinningDesc',
            image: '/images/categories/yarn.jpg',
            link: '/product-catalog?category=yarn',
            gradient: 'from-blue-600 to-blue-700',
        },
        {
            id: 'fabrics',
            nameKey: 'fabricsTextiles',
            descKey: 'fabricsTextilesDesc',
            image: '/images/categories/fabrics.jpg',
            link: '/product-catalog?category=fabrics',
            gradient: 'from-amber-600 to-amber-700',
        },
        {
            id: 'accessories',
            nameKey: 'accessoriesTrims',
            descKey: 'accessoriesTrimsDesc',
            image: '/images/categories/accessories.jpg',
            link: '/product-catalog?category=accessories',
            gradient: 'from-green-600 to-green-700',
        },
        {
            id: 'machinery',
            nameKey: 'machineryEquipment',
            descKey: 'machineryEquipmentDesc',
            image: '/images/categories/machinery.jpg',
            link: '/product-catalog?category=machinery',
            gradient: 'from-purple-600 to-purple-700',
        },
    ];

    return (
        <section className="py-20 px-6 bg-gradient-to-b from-stone-900 via-stone-800 to-stone-900">
            <div className="max-w-7xl mx-auto">
                {/* Section Header */}
                <div className="text-center mb-16">
                    <span className="inline-block px-6 py-2 bg-amber-600/20 border border-amber-500/40 text-amber-300 rounded-full text-sm font-semibold tracking-widest uppercase mb-6">
                        {t('industryCategories')}
                    </span>
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 font-serif">
                        {t('whatWouldYouLikeToDo')}
                    </h2>
                    <p className="text-xl text-stone-300 max-w-3xl mx-auto">
                        {t('categoriesDesc')}
                    </p>
                </div>

                {/* Category Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {categories.map((category) => (
                        <Link
                            key={category.id}
                            href={category.link}
                            className="group relative overflow-hidden rounded-2xl bg-stone-900 border-2 border-stone-700 hover:border-amber-500/50 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-amber-500/20"
                        >
                            {/* Category Image */}
                            <div className="relative h-48 overflow-hidden">
                                <div
                                    className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                                    style={{
                                        backgroundImage: `url('${category.image}')`,
                                    }}
                                />
                                {/* Gradient Overlay */}
                                <div className={`absolute inset-0 bg-gradient-to-t ${category.gradient} opacity-60 group-hover:opacity-40 transition-opacity duration-300`} />

                                {/* Category Name on Image */}
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <h3 className="text-4xl md:text-5xl font-bold text-white drop-shadow-2xl text-center px-4">
                                        {t(category.nameKey as any)}
                                    </h3>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="relative p-6 bg-gradient-to-b from-stone-800 to-stone-900">
                                {/* Description */}
                                <p className="text-sm text-stone-300 leading-relaxed mb-4">
                                    {t(category.descKey as any)}
                                </p>

                                {/* Explore Link */}
                                <div className="flex items-center gap-2 text-amber-500 font-semibold text-sm group-hover:gap-3 transition-all duration-300">
                                    <span>{t('exploreCategory')}</span>
                                    <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </div>
                            </div>

                            {/* Decorative Corner */}
                            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-amber-500/10 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        </Link>
                    ))}
                </div>

                {/* Bottom CTA */}
                <div className="text-center mt-12">
                    <p className="text-stone-400 mb-4">{t('noResultsFound')}?</p>
                    <Link
                        href="/product-catalog"
                        className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-amber-600 to-amber-500 text-white rounded-xl font-bold hover:from-amber-500 hover:to-amber-400 transition-all duration-300 shadow-xl hover:shadow-amber-500/50"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <span>{t('browseProductsAction')}</span>
                    </Link>
                </div>
            </div>
        </section>
    );
};

export default ProductCategories;
